import * as React from "react";

import { cn } from "../../lib/utils";
import { type VariantProps, cva } from "class-variance-authority";

type InputStatus = "warning";

const inputVariants = cva(
  "flex h-8 w-full min-w-0 rounded-md border px-3 py-1.5 text-sm shadow-xs outline-none transition-[color,box-shadow,background-color,border-color] selection:bg-primary selection:text-primary-foreground placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 data-[status=warning]:border-warning data-[status=warning]:text-warning not-read-only:data-[status=warning]:focus-visible:border-warning not-read-only:data-[status=warning]:focus-visible:ring-[3px] not-read-only:data-[status=warning]:focus-visible:ring-warning/40 data-[status=warning]:not-read-only:data-[hover=true]:border-warning not-read-only:data-[status=warning]:data-[focus=true]:border-warning not-read-only:data-[status=warning]:data-[focus=true]:ring-[3px] not-read-only:data-[status=warning]:data-[focus=true]:ring-warning/40 aria-invalid:border-destructive aria-invalid:text-destructive not-read-only:aria-invalid:focus-visible:border-destructive not-read-only:aria-invalid:focus-visible:ring-[3px] not-read-only:aria-invalid:focus-visible:ring-destructive/20 aria-invalid:not-read-only:data-[hover=true]:border-destructive not-read-only:aria-invalid:data-[focus=true]:border-destructive not-read-only:aria-invalid:data-[focus=true]:ring-[3px] not-read-only:aria-invalid:data-[focus=true]:ring-destructive/20 aria-invalid:data-[status=warning]:border-destructive aria-invalid:data-[status=warning]:text-destructive not-read-only:aria-invalid:data-[status=warning]:focus-visible:border-destructive not-read-only:aria-invalid:data-[status=warning]:focus-visible:ring-destructive/20 aria-invalid:data-[status=warning]:not-read-only:data-[hover=true]:border-destructive not-read-only:aria-invalid:data-[status=warning]:data-[focus=true]:border-destructive not-read-only:aria-invalid:data-[status=warning]:data-[focus=true]:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
  {
    variants: {
      variant: {
        default:
          "border-border bg-background enabled:[&[readonly]]:bg-transparent enabled:[&[readonly]]:shadow-none not-read-only:focus-visible:border-control-focus not-read-only:focus-visible:ring-0 not-read-only:data-[hover=true]:border-ring not-read-only:data-[focus=true]:border-control-focus not-read-only:data-[focus=true]:ring-0 dark:bg-input/30 dark:enabled:[&[readonly]]:bg-transparent",
        ghost:
          "border-transparent bg-transparent shadow-none not-read-only:hover:bg-accent not-read-only:hover:text-accent-foreground not-read-only:focus-visible:border-control-focus not-read-only:focus-visible:bg-background not-read-only:focus-visible:ring-0 not-read-only:data-[hover=true]:bg-accent not-read-only:data-[hover=true]:text-accent-foreground not-read-only:data-[focus=true]:border-control-focus not-read-only:data-[focus=true]:bg-background not-read-only:data-[focus=true]:ring-0 read-only:ring-0!",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type InputProps = Omit<React.ComponentProps<"input">, "size"> &
  VariantProps<typeof inputVariants> & {
    "data-status"?: InputStatus;
    status?: InputStatus;
  };

function Input({
  className,
  type,
  readOnly,
  placeholder,
  variant,
  status,
  "data-status": dataStatus,
  ...props
}: InputProps) {
  return (
    <input
      type={type}
      data-slot="input"
      data-status={dataStatus ?? status}
      className={cn(inputVariants({ variant }), className)}
      {...props}
      readOnly={readOnly}
      placeholder={readOnly ? undefined : placeholder}
    />
  );
}

export { Input };
export type { InputProps, InputStatus };
